import type { ResultInput, ResultOutput, Rule, RuleSet } from '@mahjong/core';

/**
 * 本番では SPA と API を同じ CloudFront から配信しているため、既定の '/v1' が
 * そのまま同一オリジンの API を指す。Vite dev server から実 API を叩く場合だけ
 * .env で execute-api のエンドポイントを与える。
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/v1';

/** §8.2 のエラーレスポンスに対応する例外 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  /** トークンが無効、またはこのグループを読む権限がない */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** 他の端末が先に更新した（§10 同時編集） */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** サーバー側が未実装（フェーズ3以降で実装するエンドポイント） */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }
}

export interface Member {
  memberId: string;
  name: string;
  order: number;
  active: boolean;
  createdAt: string;
}

export interface Group {
  groupId: string;
  name: string;
  defaultRuleId: string;
  memberCount: number;
  matchCount: number;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface GroupBundle {
  group: Group;
  rules: RuleSet[];
  members: Member[];
}

export interface CreateGroupResponse extends GroupBundle {
  groupId: string;
  /** 平文トークンが返るのはこの1回だけ。localStorage に保存して共有URLに載せる */
  token: string;
  sharePath: string;
}

export interface Match {
  matchId: string;
  playedAt: string;
  memo?: string;
  version: number;
  /** 登録時点のルールのスナップショット。後からルールを変えても計算は変わらない */
  ruleSnapshot: Rule;
  results: ResultOutput[];
}

export interface CreateGroupInput {
  name: string;
  playerCount: 3 | 4;
  umaPreset?: string;
  memberNames: string[];
}

export interface CreateMatchInput {
  playedAt?: string;
  ruleId?: string;
  memo?: string;
  results: ResultInput[];
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  token?: string;
  body?: unknown;
  signal?: AbortSignal;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', token, body, signal } = options;

  const headers = new Headers({ accept: 'application/json' });
  if (body !== undefined) headers.set('content-type', 'application/json');
  // トークンはヘッダーでのみ送る。URL に載せるとサーバーログや Referer に残る（§4.1）
  if (token) headers.set('X-Group-Token', token);

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'NETWORK_ERROR', '通信に失敗しました。電波状況をご確認ください', {
      cause,
    });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeJson(text) : null;

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; detail?: unknown } } | null)
      ?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `エラーが発生しました（${res.status}）`,
      err?.detail,
    );
  }

  return payload as T;
}

export const api = {
  /** グループ作成（§4.1 G-01）。認証不要な唯一の書き込み */
  createGroup(input: CreateGroupInput, signal?: AbortSignal) {
    return request<CreateGroupResponse>('/groups', { method: 'POST', body: input, signal });
  },

  /** グループ情報＋メンバー＋ルールの一括取得（§7.3 アクセスパターン2） */
  getGroup(groupId: string, token: string, signal?: AbortSignal) {
    return request<GroupBundle>(`/groups/${groupId}`, { token, signal });
  },

  /** グループ名の変更と既定ルールの切り替え（G-04 / §4.3） */
  updateGroup(
    groupId: string,
    token: string,
    patch: { name?: string; defaultRuleId?: string },
  ) {
    return request<Group>(`/groups/${groupId}`, { method: 'PATCH', token, body: patch });
  },

  /** グループ削除（G-04）。配下のメンバー・ルール・対局もすべて消える */
  deleteGroup(groupId: string, token: string) {
    return request<void>(`/groups/${groupId}`, { method: 'DELETE', token });
  },

  /** 対局一覧（新しい順）。フェーズ4でサーバー側を実装する */
  listMatches(
    groupId: string,
    token: string,
    params: { from?: string; to?: string; limit?: number; cursor?: string } = {},
    signal?: AbortSignal,
  ) {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    return request<{ matches: Match[]; cursor?: string }>(
      `/groups/${groupId}/matches${qs ? `?${qs}` : ''}`,
      { token, signal },
    );
  },

  /** 対局登録。ポイント類はサーバーが計算するため送らない（§8.1） */
  createMatch(groupId: string, token: string, input: CreateMatchInput, signal?: AbortSignal) {
    return request<Match>(`/groups/${groupId}/matches`, {
      method: 'POST',
      token,
      body: input,
      signal,
    });
  },

  getMatch(groupId: string, token: string, matchId: string, signal?: AbortSignal) {
    return request<Match>(`/groups/${groupId}/matches/${matchId}`, { token, signal });
  },

  /**
   * 対局更新（R-04）。version は楽観ロックに使い、他の端末が先に更新していれば
   * 409 VERSION_CONFLICT になる。ルールは登録時のスナップショットのまま。
   */
  updateMatch(
    groupId: string,
    token: string,
    matchId: string,
    input: CreateMatchInput & { version: number },
  ) {
    return request<Match>(`/groups/${groupId}/matches/${matchId}`, {
      method: 'PUT',
      token,
      body: input,
    });
  },

  deleteMatch(groupId: string, token: string, matchId: string) {
    return request<void>(`/groups/${groupId}/matches/${matchId}`, { method: 'DELETE', token });
  },

  // --- メンバー（§4.2） ---------------------------------------------------

  createMember(groupId: string, token: string, name: string) {
    return request<Member>(`/groups/${groupId}/members`, {
      method: 'POST',
      token,
      body: { name },
    });
  },

  updateMember(
    groupId: string,
    token: string,
    memberId: string,
    patch: { name?: string; active?: boolean; order?: number },
  ) {
    return request<Member>(`/groups/${groupId}/members/${memberId}`, {
      method: 'PATCH',
      token,
      body: patch,
    });
  },

  /** 対局履歴があるメンバーは 409 MEMBER_IN_USE になる（M-04） */
  deleteMember(groupId: string, token: string, memberId: string) {
    return request<void>(`/groups/${groupId}/members/${memberId}`, { method: 'DELETE', token });
  },

  /** ルール更新（§4.3）。過去の対局はスナップショットを持つので影響を受けない */
  updateRule(
    groupId: string,
    token: string,
    ruleId: string,
    rule: Rule & { name?: string; umaPreset?: string },
  ) {
    return request<RuleSet>(`/groups/${groupId}/rules/${ruleId}`, {
      method: 'PUT',
      token,
      body: rule,
    });
  },

  createRule(groupId: string, token: string, rule: Rule & { name?: string; umaPreset?: string }) {
    return request<RuleSet>(`/groups/${groupId}/rules`, { method: 'POST', token, body: rule });
  },

  /** 既定ルールと最後の1件は削除できない */
  deleteRule(groupId: string, token: string, ruleId: string) {
    return request<void>(`/groups/${groupId}/rules/${ruleId}`, { method: 'DELETE', token });
  },
};
